import React, { useState, useMemo } from 'react';
import { Send, Terminal, ShieldAlert } from 'lucide-react';
import { edgeDeviceService } from '../../../services/edgeDeviceService';
import { guardAtCommand } from '@simcheck/shared-types';

interface QuickCommandPanelProps {
  deviceId: string;
  modems: Array<{ id: string; imei?: string; model?: string }>;
  onCommandSent?: () => void;
}

const commonCommands = [
  { label: 'Signal Quality', command: 'AT+CSQ' },
  { label: 'Network Info', command: 'AT+COPS?' },
  { label: 'IMEI', command: 'AT+CGSN' },
  { label: 'SIM Status', command: 'AT+CPIN?' }
];

export const QuickCommandPanel: React.FC<QuickCommandPanelProps> = ({
  deviceId,
  modems,
  onCommandSent
}) => {
  const [command, setCommand] = useState('');
  const [selectedModem, setSelectedModem] = useState(() => {
    // Pre-select the first modem if there's only one
    return modems && modems.length === 1 ? (modems[0].imei || modems[0].id) : '';
  });
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<{ success: boolean; message: string } | null>(null);

  // Pre-check the command against the AT safety policy so we can surface
  // the same decision the server would make, inline, without round-tripping.
  // The server still enforces — this is purely UX.
  const policy = useMemo(() => {
    const trimmed = command.trim();
    if (!trimmed) return null;
    return guardAtCommand(trimmed);
  }, [command]);

  const isBlocked = policy?.action === 'block';

  const handleSendCommand = async () => {
    if (!command.trim()) return;
    if (isBlocked) return;
    
    try {
      setSending(true);
      setLastResult(null);

      await edgeDeviceService.sendCommand(
        deviceId,
        selectedModem || '',
        command.trim()
      );

      setLastResult({ success: true, message: 'Command sent successfully' });
      setCommand('');
      
      // Notify parent to refresh responses
      if (onCommandSent) {
        setTimeout(onCommandSent, 500); // Small delay to let the command process
      }
    } catch (error) {
      console.error('Error sending command:', error);
      setLastResult({ 
        success: false, 
        message: error instanceof Error ? error.message : 'Failed to send command'
      });
    } finally {
      setSending(false);
    }
  };

  const handleQuickCommand = (cmd: string) => {
    setCommand(cmd);
  };

  return (
    <div className="bg-accent-blue-faded border border-border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <Terminal className="w-5 h-5 text-accent-blue" />
        <h3 className="text-sm font-semibold text-foreground">Quick Command</h3>
      </div>

      {/* Modem Selector */}
      {modems && modems.length > 0 && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-muted-foreground mb-1">
            Target Modem {modems.length === 1 ? '' : '(optional)'}
          </label>
          <select
            value={selectedModem}
            onChange={(e) => setSelectedModem(e.target.value)}
            className="w-full px-2 py-1.5 text-sm border border-border rounded focus:ring-2 focus:ring-accent-blue focus:border-transparent bg-card text-foreground"
          >
            {modems.length > 1 && <option value="">All Modems</option>}
            {modems.map((modem) => (
              <option key={modem.id} value={modem.imei || modem.id}>
                {modem.model || modem.imei || modem.id}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Command Input */}
      <div className="mb-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSendCommand()}
            placeholder="Enter AT command (e.g., AT+CSQ)"
            disabled={sending}
            className="flex-1 px-3 py-2 text-sm border border-border rounded focus:ring-2 focus:ring-accent-blue focus:border-transparent disabled:bg-muted disabled:cursor-not-allowed bg-card text-foreground placeholder:text-muted-foreground/70"
          />
          <button
            onClick={handleSendCommand}
            disabled={sending || !command.trim() || isBlocked}
            className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white text-sm font-medium rounded hover:bg-accent-blue-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className={`w-4 h-4 ${sending ? 'animate-pulse' : ''}`} />
            {sending ? 'Sending...' : 'Send'}
          </button>
        </div>
        {isBlocked && (
          <div className="mt-2 flex items-start gap-2 p-2 rounded border border-status-error/40 bg-status-error-bg">
            <ShieldAlert className="w-4 h-4 text-status-error mt-0.5 flex-shrink-0" />
            <div className="text-xs">
              <div className="font-medium text-status-error">Blocked by safety policy</div>
              <div className="text-muted-foreground mt-0.5">
                {policy?.blockReason}{' '}
                <a
                  href="https://github.com/shvartzj1/simcheck/blob/main/docs/guides/reference/AT_SAFETY_POLICY.md"
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent-blue hover:underline"
                >
                  Learn more
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Command Buttons */}
      <div className="flex flex-wrap gap-2 mb-3">
        {commonCommands.map((cmd) => (
          <button
            key={cmd.command}
            onClick={() => handleQuickCommand(cmd.command)}
            disabled={sending}
            className="px-2 py-1 text-xs font-medium bg-card border border-border text-accent-blue rounded hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cmd.label}
          </button>
        ))}
      </div>

      {/* Result Message */}
      {lastResult && (
        <div className={`
          p-2 text-xs rounded
          ${lastResult.success 
            ? 'bg-accent-green-faded text-accent-light-green dark:text-accent-green border border-border' 
            : 'bg-accent-red-faded text-accent-light-red dark:text-accent-red border border-border'
          }
        `}>
          {lastResult.message}
        </div>
      )}
    </div>
  );
};

